import { FlixMediaUI } from "./FlixMediaUI";
import { StandardProps } from "../componentMap";
import React, { useEffect, useRef, useState } from "react";
import { useVariant } from "@/hooks/useVariant/useVariant.ts";
import { attachScript, cleanUp } from "./utils";
import { dispatchAnalyticsEvent } from "@/utils";
import { noticeError } from "@/utils/newrelic/requests.ts";
import { recordImpressionByParam } from "@JohnLewisPartnership/experiments-utils";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ";
import { useMonetateId } from "@/hooks/useMonetateId/useMonetateId.ts";

export function FlixMedia({
  product,
  runtimeConfig,
  experiencesRawObject,
  flags,
}: Pick<
  StandardProps,
  "product" | "runtimeConfig" | "flags" | "experiencesRawObject"
>) {
  const showFlixMedia = flags?.showFlixMedia.value === true;
  const observeFlixMedia = flags?.observeFlixMedia.value === true;
  const flixMediaDefaultOpen = flags.flixMediaDefaultOpen.value === true;

  const { variant } = useVariant(product);
  const flixMediaContainerRef = useRef<HTMLDivElement>(null);
  const flixMediaInpageRef = useRef<HTMLDivElement>(null);
  const [isFlixAvailable, setIsFlixAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(flixMediaDefaultOpen);
  const { monetateId } = useMonetateId();
  const { skuId } = variant.aliases;
  const brand = product.brand?.name || "";
  const { price, title } = variant;
  const value = price?.value || "0.00";
  const {
    browsePageHierarchy,
    aliases: { pimProductId },
  } = product;
  const category = browsePageHierarchy[1]
    ? browsePageHierarchy[1].name
    : browsePageHierarchy[0]?.name || "";

  const recordImpressionFlixMedia = (
    key:
      | "pdp-scaffold-flixMediaContentAvailable"
      | "pdp-scaffold-flixMediaContentWatched",
    value: string,
  ) => {
    if (observeFlixMedia) {
      recordImpressionByParam(
        runtimeConfig?.monetateEnvironment,
        experiencesRawObject as [],
        key,
        value,
        monetateId,
      );
    }
  };

  useEffect(() => {
    recordImpressionFlixMedia("pdp-scaffold-flixMediaContentWatched", "false");
    return () => cleanUp();
  }, []);

  useEffect(() => {
    const url = `${runtimeConfig.flixMediaBaseUrl}/${skuId}`;
    fetch(url)
      .then((res) => {
        res.json().then((json) => {
          const matchFound = json["event"] === "matchhit";

          recordImpressionFlixMedia(
            "pdp-scaffold-flixMediaContentAvailable",
            matchFound.toString(),
          );

          if (matchFound) {
            setIsLoading(true);
          }
          setIsFlixAvailable(matchFound);
        });
      })
      .catch((e: Error) => {
        noticeError("Could not successfully retrieve flixMedia data", {
          message: e.message,
          stack: e.stack || "",
        });
        setIsLoading(false);
        setIsFlixAvailable(false);
      });

    return () => {
      cleanUp();
    };
  }, [skuId]);

  useEffect(() => {
    if (
      !isLoading ||
      !flixMediaInpageRef.current ||
      !flixMediaContainerRef.current
    ) {
      return;
    }

    const setContentLoaded = (observer: MutationObserver) => {
      observer.disconnect();
      setIsLoading(false);
    };

    const observer = new MutationObserver((_, observer) =>
      setContentLoaded(observer),
    );

    observer.observe(flixMediaInpageRef.current, {
      childList: true,
      attributes: true,
      subtree: true,
    });

    attachScript(flixMediaContainerRef.current, skuId, brand.toLowerCase());

    return () => {
      observer.disconnect();
    };
  }, [isLoading]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
    flixMediaContainerRef.current?.scrollIntoView();

    if (!isOpen) {
      dispatchAnalyticsEvent(
        "global.interaction",
        {
          type: "pdp",
          subType: "take a closer look - show more",
          additionalInfo: {},
          properties: {},
          products: [
            {
              brand: brand,
              category: category,
              sku: skuId,
              price: value,
              productName: title,
              productId: pimProductId,
            },
          ],
        },
        {},
      );
    }

    dispatchMonetateQEvent("pdp:flixmedia:showmore:button:click");
    recordImpressionFlixMedia("pdp-scaffold-flixMediaContentWatched", "true");
  };

  if (!showFlixMedia) return null;

  return (
    isFlixAvailable && (
      <FlixMediaUI
        isLoading={isLoading}
        isOpen={isOpen}
        handleToggle={handleToggle}
        flixMediaContainerRef={flixMediaContainerRef}
        flixMediaInpageRef={flixMediaInpageRef}
      />
    )
  );
}
